import { Tabs, TabsContent, TabsList, TabsTrigger, RichTextEditor, Input, RequiredMark } from '@/components/ui';
import {
  BASE_CONTENT_LOCALE,
  CONTENT_LOCALES,
  CONTENT_LOCALE_LABEL,
  type ContentLocale,
} from '../lib/contentLocales';

/**
 * 선택적 per-locale 제목(텍스트) 필드 설정. 본문(HTML) 위에 로케일별 제목 입력을 함께 노출한다.
 * 이벤트/당첨자처럼 translations 가 {title, body} 인 계약에서 사용한다.
 */
export interface LocaleTitleFieldConfig {
  label: string;
  /** 정본(ko) 제목. */
  baseValue: string;
  onBaseChange: (value: string) => void;
  /** 번역 로케일별 제목(en/ja/zh). */
  translations: Partial<Record<ContentLocale, string>>;
  onTranslationChange: (locale: ContentLocale, value: string) => void;
  placeholder?: string;
  maxLength?: number;
}

export interface LocaleHtmlTabsProps {
  /** 정본(ko-KR) 본문 HTML. */
  baseValue: string;
  /** 정본 본문 변경. */
  onBaseChange: (html: string) => void;
  /** 번역 로케일별 본문 HTML 맵(en-US/ja-JP/zh-CN). 누락 키 = 빈 본문. */
  translations: Partial<Record<ContentLocale, string>>;
  /** 특정 로케일 본문 변경. 빈 문자열이면 호스트가 맵에서 제거하거나 빈값 저장(=SF ko 폴백). */
  onTranslationChange: (locale: ContentLocale, html: string) => void;
  /** 본문 영역 최소 높이(px). */
  minHeight?: number;
  /** 정본 탭의 이미지 업로드 핸들러(번역 탭은 정본과 동일 핸들러 공유). 없으면 URL 프롬프트 폴백. */
  onUploadImage?: (file: File) => Promise<string>;
  onUploadError?: (error: unknown) => void;
  /** 본문 이미지 업로드 안내(RichTextEditor imageGuide 로 전달). */
  imageGuide?: string;
  /** 접근성 라벨 프리픽스(예: "이벤트 본문"). 로케일명이 뒤에 붙는다. */
  ariaLabelPrefix?: string;
  /** 선택적 per-locale 제목 필드(본문 위에 노출). translations 가 {title, ...} 인 계약에서 사용. */
  titleField?: LocaleTitleFieldConfig;
}

/**
 * QA-122 (b안) — CMS 콘텐츠 본문 다국어 입력 탭.
 *
 * ko/en/ja/zh 로케일 탭마다 공용 `RichTextEditor`(HTML 소스 직접 편집 지원)를 렌더한다.
 * ko-KR 은 정본(top-level 필드), 나머지 로케일은 translations 맵에 바인딩하며 비우면
 * 스토어프론트가 ko-KR 로 폴백한다. 계약 필드명(translations[locale]=html)은 호스트 폼이
 * baseValue/translations 매핑으로 주입하므로, 본 컴포넌트는 BE 필드명에 의존하지 않는다.
 *
 * 백오피스 UI 는 한국어지만 탭 라벨은 각 로케일 자국 표기(한국어/English/日本語/中文)를 쓴다.
 */
export function LocaleHtmlTabs({
  baseValue,
  onBaseChange,
  translations,
  onTranslationChange,
  minHeight = 280,
  onUploadImage,
  onUploadError,
  imageGuide,
  ariaLabelPrefix,
  titleField,
}: LocaleHtmlTabsProps) {
  const label = (loc: ContentLocale) =>
    ariaLabelPrefix ? `${ariaLabelPrefix} (${CONTENT_LOCALE_LABEL[loc]})` : CONTENT_LOCALE_LABEL[loc];

  return (
    <Tabs defaultValue={BASE_CONTENT_LOCALE} className="w-full">
      <TabsList className="mb-3">
        {CONTENT_LOCALES.map((loc) => (
          <TabsTrigger key={loc} value={loc}>
            {CONTENT_LOCALE_LABEL[loc]}
            {loc === BASE_CONTENT_LOCALE ? <RequiredMark /> : null}
          </TabsTrigger>
        ))}
      </TabsList>

      {CONTENT_LOCALES.map((loc) => {
        const isBase = loc === BASE_CONTENT_LOCALE;
        return (
          <TabsContent key={loc} value={loc} className="flex flex-col gap-2">
            {!isBase ? (
              <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                비우면 한국어(정본) 본문이 노출됩니다.
              </p>
            ) : null}
            {titleField ? (
              <div className="flex flex-col gap-1">
                <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                  {titleField.label} ({CONTENT_LOCALE_LABEL[loc]})
                  {isBase ? <RequiredMark /> : null}
                </span>
                <Input
                  value={isBase ? titleField.baseValue : titleField.translations[loc] ?? ''}
                  onChange={(e) =>
                    isBase
                      ? titleField.onBaseChange(e.target.value)
                      : titleField.onTranslationChange(loc, e.target.value)
                  }
                  placeholder={titleField.placeholder}
                  maxLength={titleField.maxLength}
                  aria-label={`${titleField.label} (${CONTENT_LOCALE_LABEL[loc]})`}
                />
              </div>
            ) : null}
            <RichTextEditor
              value={isBase ? baseValue : translations[loc] ?? ''}
              onChange={(html) =>
                isBase ? onBaseChange(html) : onTranslationChange(loc, html)
              }
              minHeight={minHeight}
              aria-label={label(loc)}
              {...(onUploadImage ? { onUploadImage } : {})}
              {...(onUploadError ? { onUploadError } : {})}
              {...(imageGuide ? { imageGuide } : {})}
            />
          </TabsContent>
        );
      })}
    </Tabs>
  );
}
